<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { computed, ref, unref, watch } from 'vue';
import { onBeforeRouteLeave } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useForm } from 'vee-validate';
import * as yup from 'yup';
import { Notify } from 'quasar';

import { useCommunityStore, useProjectStore, useProjectTemplateStore } from '@/stores';
import type { Locales, ProjectDto, UserPermissionDto } from '@/services/types/api.types';
import type { SavingOptions } from '@/services/types/file-uploader.types';
import { useLanguageValidationErrors } from '@/composables';
import { useModuleBreadcrumbOptions } from '@/composables/breadcrumb-options';
import { useWatchItemLoading } from '@/composables/watch-item-loading';
import { useRouteNumberParam } from '@/composables/use-route-param';
import { wrapSavingOperation } from '@/helpers/notifications.helper';
import { getLanguageEnabled, getTranslation, provideEditLocale } from '@/composables/data-display-utils';
import { useThemeOptions } from '@/composables/useThemeOptions';
import { languageEnabledArray, localizableString, slug } from '@/helpers/validation.helper';
import { formSaveImageGuard, formSaveNavigationGuard, resetFormToItem } from '@/helpers/form.helper';
import type { BreadcrumbElement } from '@/helpers/breadcrumbs.helper';
import { Constants } from '@/constants';
import type { BlockParent, BlockRouteFor } from '@/types/modules';

import ImageField from '@/components/ImageField.vue';
import FormActions from '@/components/FormActions.vue';
import DynamicTabs from '@/components/DynamicTabs.vue';
import LocalizedInput from '@/components/LocalizedInput.vue';
import LocalizedSelect from '@/components/base/LocalizedSelect.vue';
import BaseDateInput from '@/components/base/BaseDateInput.vue';
import StandardInput from '@/components/StandardInput.vue';
import EmailList from '@/components/EmailList.vue';
import ModuleList from '@/components/ModuleList.vue';
import LocalizedEditor from '@/components/base/LocalizedEditor.vue';
import UserPermissionList from '@/components/UserPermissionList.vue';
import StandardToggle from '@/components/StandardToggle.vue';
import LanguageSelector from '@/components/LanguageSelector.vue';
import TopPageNavigation from '@/components/TopPageNavigation.vue';
import { cloneDeep } from 'lodash';

// PROPS
const props = defineProps<{ isTemplate: boolean }>();

const { t } = useI18n();

// STORE
const communityStore = useCommunityStore();
const store = props.isTemplate ? useProjectTemplateStore() : useProjectStore();

// REF
const languageEnabled = ref<Locales[]>([]);
const { community } = storeToRefs(communityStore);

// CONST
const projectId = useRouteNumberParam('id');
// A template is a project: its blocks are requested the same way.
const blockParent = computed<BlockParent>(() => ({ type: Constants.Block.ParentType.Project, id: projectId.value }));
// A template's blocks open on their own route, whose way back leads to the template.
const blockRouteFor: BlockRouteFor = (blockId) => ({
  name: props.isTemplate ? 'template_module_details' : 'module_details',
  params: { projectId: projectId.value, moduleId: blockId },
});

let validationSchema;

if (props.isTemplate) {
  validationSchema = computed(() =>
    yup.object<ProjectDto>({
      title: localizableString(languageEnabled.value),
      slug: slug(),
      description: localizableString(languageEnabled.value),
      options: yup
        .object()
        .shape({
          show_private_everyone: yup.boolean().required(),
        })
        .required(),
      language_enabled: languageEnabledArray(community.value?.language_enabled),
    }),
  );
} else {
  validationSchema = computed(() =>
    yup.object<ProjectDto>({
      title: localizableString(languageEnabled.value),
      slug: slug(),
      description: localizableString(languageEnabled.value),
      status: yup.number().required('Status is required when it is not a template'),
      visibility: yup.number().required('Visibility is required when it is not a template'),
      begin_date: yup.string().when('options.automatic_publish', {
        is: true,
        then: (schema) => schema.required('Begin date is required when automatic publish is enabled'),
        otherwise: (schema) => schema.nullable(),
      }),
      end_date: yup.string().nullable(),
      private_email_allowed: yup
        .object()
        .shape({
          // See consultationSchema.ts: `.required()` here only fires while the form is still
          // loading, and the stale error can never be cleared, which disables Save.
          email: yup.array().of(yup.string().matches(Constants.EmailRegex)),
          domain: yup.array().of(yup.string()),
        })
        .required(),
      options: yup
        .object()
        .shape({
          show_private_everyone: yup.boolean().required(),
        })
        .required(),
      open_graph_file: yup
        .object()
        .shape({
          url: yup.string().optional(),
          altText: localizableString(languageEnabled.value, { optional: true }),
        })
        .nullable(),
      banner_file: yup
        .object()
        .shape({
          url: yup.string().optional(),
          altText: localizableString(languageEnabled.value, { optional: true }),
        })
        .nullable(),
      language_enabled: languageEnabledArray(community.value?.language_enabled),
    }),
  );
}

// FORM
const formContext = useForm({ validationSchema });
const { handleSubmit, errors, meta, values, resetForm, setFieldValue } = formContext;
const project = useWatchItemLoading<ProjectDto>(projectId, store, (item) => {
  const values = cloneDeep(item);
  languageEnabled.value = getLanguageEnabled(values);
  resetFormToItem(formContext, values);
});

const { moduleOptions } = useModuleBreadcrumbOptions(blockParent, blockRouteFor);

const languageErrors = useLanguageValidationErrors(errors);

const editLocale = provideEditLocale(languageEnabled);

// LIFECYCLE
watch(
  projectId,
  async (id) => {
    if (id < 0) return;
    await store.fetchById(id);
  },
  { immediate: true },
);

onBeforeRouteLeave(formSaveNavigationGuard(meta));

// COMPUTED
// A template has no status, so its store has no statuses to offer: read them from the project store.
const statusOptions = computed(() => useProjectStore().getProjectStatuses());
const themeOptions = useThemeOptions('project', languageEnabled);

const isDirty = computed(() => {
  return unref(meta).dirty;
});

const tabs = computed(() => {
  const tabList = [
    { id: 'infos', name: t('project.tabs.infos'), isDirty: isDirty.value, icon: 'o_info' },
    { id: 'modules', name: t('project.tabs.modules'), icon: 'o_widgets' },
    { id: 'images', name: t('project.tabs.images'), icon: 'o_image' },
    { id: 'options', name: t('project.tabs.options'), icon: 'more_vert' },
  ];

  if (!props.isTemplate) {
    tabList.push({ id: 'permissions', name: t('project.tabs.permissions'), icon: 'groups' });
  }

  return tabList;
});

const openedTab = computed(() => {
  if (window.location.hash) {
    return window.location.hash.substring(1);
  }
  return 'infos';
});

const breadcrumbs = computed(() => {
  const breadcrumbItems: BreadcrumbElement[] = [
    {
      label: project.value?.title,
      to: {
        name: props.isTemplate ? 'project_template_details' : 'project_details',
        params: { id: project.value?.id },
      },
      icon: props.isTemplate ? 'o_settings' : 'o_folder_copy',
    },
  ];
  if (project.value?.id) {
    breadcrumbItems.push({
      label: t('breadcrumbs.modules'),
      to: undefined,
      icon: 'o_folder',
      options: moduleOptions.value,
    });
  }
  return breadcrumbItems;
});

// FUNCTIONS
const handleSaveBanner = async (options: SavingOptions) => {
  wrapSavingOperation(async () => await store.saveBanner(projectId.value, options));
};

const handleSaveOpenGraph = async (options: SavingOptions) => {
  wrapSavingOperation(async () => await store.saveOpenGraph(projectId.value, options));
};

const removeBanner = async () => {
  await store.removeBanner(projectId.value);
};

const removeOpenGraph = async () => {
  await store.removeOpenGraph(projectId.value);
};

const handleClearBanner = async () => {
  formSaveImageGuard(isDirty.value, removeBanner);
};

const handleClearOpenGraph = async () => {
  formSaveImageGuard(isDirty.value, removeOpenGraph);
};

const onSubmit = handleSubmit(async (values) => {
  console.debug('submitting', values);
  wrapSavingOperation(
    async () => await store.save(values as ProjectDto),
    () => resetForm({ values: project.value! }),
  );
});

const onCancel = () => {
  if (project.value) {
    resetForm({ values: project.value });
  } else {
    console.warn('onCancel: project is null');
  }
};

const preview = () => {
  const previewUrl = store.previewURL(projectId.value);
  if (previewUrl) {
    window.open(previewUrl, '_blank')?.focus();
  }
};

function updateEmailList(emails: string[]) {
  setFieldValue('private_email_allowed.email', emails);
}

const fetchUserPermissions = async () => {
  if (!project.value) {
    return [];
  }

  if (!project.value.user_permissions) {
    await store.fetchUserPermissions(projectId.value);
  }

  // get the project object straight from the store since we are using a clone in the rest of the page
  // how can we improve? could we encapsulate better? or refactor?
  const p = store.getItem(projectId.value);
  return p!.user_permissions;
};

const addUserPermission = async (email: string, role: number): Promise<boolean> => {
  const success = await store.addUserPermission(projectId.value, email, role);
  if (!success) {
    Notify.create({
      type: 'warning',
      message: t('project.permission.alreadyAdmin'),
      position: 'top',
      timeout: 5000,
    });
  }
  return true;
};

const deleteUserPermission = async (up: UserPermissionDto) => {
  await store.deleteUserPermission(up);
};
</script>

<template>
  <q-page class="project-detail">
    <top-page-navigation
      :breadcrumbs="breadcrumbs"
      :language-enabled="languageEnabled"
      to="/projects"
      @preview="preview"
    />
    <div class="title">
      <div>
        <q-icon :name="props.isTemplate ? 'o_settings' : 'o_folder_copy'" />
        <h1>{{ getTranslation(project, 'title', languageEnabled) }}</h1>
      </div>
    </div>
    <div v-if="project">
      <dynamic-tabs :tabs="tabs" :active="openedTab" keep-alive>
        <template #infos>
          <form-actions
            :onCancel="onCancel"
            :onSubmit="onSubmit"
            :languageErrors="languageErrors"
            :meta="meta"
            v-model:editLocale="editLocale"
            :languageEnabled="languageEnabled"
          />
          <localized-input
            name="title"
            :label="t('project.title')"
            :editLocale="editLocale"
            outlined
            :language_enabled="languageEnabled"
          />
          <standard-input
            name="slug"
            :label="t('project.slug')"
            :editLocale="editLocale"
            outlined
            :language_enabled="languageEnabled"
          />
          <q-field :label="t('global.label.content')" stack-label borderless>
            <template v-slot:control>
              <localized-editor
                name="description"
                :label="t('global.label.content')"
                class="q-mt-sm full-width"
                :editLocale="editLocale"
                :language_enabled="languageEnabled"
              />
            </template>
          </q-field>
          <div v-if="!isTemplate">
            <div class="row q-gutter-md">
              <div class="col">
                <localized-select
                  name="status"
                  :label="t('project.status.label')"
                  :editLocale="editLocale"
                  :options="statusOptions"
                  emit-value
                  map-options
                  outlined
                />
              </div>
            </div>
            <div class="row q-gutter-md">
              <div class="col">
                <base-date-input name="begin_date" :label="t('project.begin_date')" />
              </div>
              <div class="col">
                <base-date-input name="end_date" :label="t('project.end_date')" />
              </div>
            </div>
            <div class="row q-gutter-md q-mb-md">
              <div class="col">
                <standard-toggle
                  name="options.automatic_publish"
                  :label="t('project.options.automatic_publish')"
                  outlined
                />
              </div>
            </div>
          </div>
        </template>
        <template #images>
          <form-actions
            :onCancel="onCancel"
            :onSubmit="onSubmit"
            :languageErrors="languageErrors"
            :meta="meta"
            v-model:editLocale="editLocale"
            :languageEnabled="languageEnabled"
          />
          <image-field
            :label="t('project.banner')"
            :source="project.banner_file?.url"
            :target-width="1920"
            :target-height="360"
            @clear-image="handleClearBanner"
            @save-image="handleSaveBanner"
            :form-dirty="isDirty"
            :language-enabled="languageEnabled"
            alt-text-name="banner_file.altText"
          />
          <image-field
            :label="t('global.label.social-media-image')"
            :source="project.open_graph_file?.url"
            :target-width="1200"
            :target-height="630"
            @clear-image="handleClearOpenGraph"
            @save-image="handleSaveOpenGraph"
            :form-dirty="isDirty"
            :language-enabled="languageEnabled"
            alt-text-name="open_graph_file.altText"
          />
        </template>
        <template #modules>
          <module-list :parent="blockParent" :block-route-for="blockRouteFor" :languageEnabled="languageEnabled" />
        </template>
        <template #options>
          <form-actions
            :show-language-switcher="false"
            v-model:editLocale="editLocale"
            :onCancel="onCancel"
            :onSubmit="onSubmit"
            :languageErrors="languageErrors"
            :meta="meta"
            :languageEnabled="languageEnabled"
          />
          <q-page>
            <q-card class="q-mb-md">
              <q-card-section>
                <q-item-label header class="text-h6">{{ t('project.general') }}</q-item-label>
              </q-card-section>
              <q-separator />
              <q-card-section>
                <localized-select
                  name="theme_key"
                  :label="t('project.theme.label')"
                  :options="themeOptions"
                  emit-value
                  map-options
                  outlined
                  clearable
                  :hint="t('project.theme.default_applied')"
                />
              </q-card-section>
            </q-card>
            <q-card class="q-mb-md">
              <q-card-section>
                <q-item-label header class="text-h6">{{ t('project.languages') }}</q-item-label>
              </q-card-section>
              <q-separator />
              <q-card-section>
                <language-selector
                  name="language_enabled"
                  :label="t('project.language_enabled')"
                  :language-enabled="community?.language_enabled"
                />
              </q-card-section>
            </q-card>
          </q-page>
        </template>
        <template #permissions>
          <form-actions
            :onCancel="onCancel"
            :onSubmit="onSubmit"
            :languageErrors="languageErrors"
            :meta="meta"
            v-model:editLocale="editLocale"
            :showLanguageSwitcher="false"
            :languageEnabled="languageEnabled"
          />
          <user-permission-list
            :fetchUserPermissions="fetchUserPermissions"
            :addUserPermission="addUserPermission"
            :deleteUserPermission="deleteUserPermission"
            :roles="[Constants.User.Role.EDITOR]"
          />
          <q-separator spaced="xl" />
          <p class="text-h6">{{ t('project.private.title') }}</p>
          <standard-toggle
            name="visibility"
            :label="t('project.private.invitation-only')"
            :true-value="Constants.Project.Visibility.Private"
            :false-value="Constants.Project.Visibility.Public"
            outlined
          />
          <standard-toggle
            name="options.show_private_everyone"
            :label="t('project.private.show-private-everyone')"
            outlined
          />
          <email-list
            :model-value="values.private_email_allowed?.email ?? []"
            :error="errors.emails"
            @update="updateEmailList"
          />
          <standard-input
            name="private_email_allowed.domainString"
            :label="t('project.private.domains')"
            type="textarea"
            outlined
          />
        </template>
      </dynamic-tabs>
    </div>
  </q-page>
</template>
<style lang="scss">
.project-detail {
  padding: 16px;

  .title {
    margin: 20px 0;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    padding: 0 10px;
    gap: 10px;

    .q-btn {
      max-height: 36px;
    }

    div {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 5px;
      max-width: 80%;

      h1 {
        display: block;
        max-width: 100%;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .q-icon {
        font-size: $large-title;
      }
    }
  }

  .information-general {
    padding: 10px;

    &__header {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      padding: 10px 0;

      .title {
        color: $color-primary;
        font-size: $small-title;
      }
    }
  }
}
</style>
